import React, { useState, useRef, useEffect } from 'react';

// `buttonClassName` replaces the compact default trigger styling when a call
// site needs a full-size button; `icon` renders before the label. Actions may
// carry `disabled`, `title` and `testId` as well as `label` and `onClick`.
const ActionDropdown = ({
  actions = [],
  label = 'Actions',
  className = '',
  buttonClassName,
  icon = null,
  disabled = false,
  title,
  'data-testid': dataTestId,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, []);

  // Destructive entries are pushed below a rule so they can't be hit by mistake.
  const isDestructive = (a) => /text-red-/.test(a.className || '');
  const primary = actions.filter((a) => !isDestructive(a));
  const destructive = actions.filter(isDestructive);

  const renderAction = (a) => (
    <button
      key={a.label}
      role="menuitem"
      data-testid={a.testId}
      disabled={a.disabled}
      title={a.title}
      onClick={() => {
        setOpen(false);
        a.onClick();
      }}
      className={`block w-full text-left px-2.5 py-2 rounded-lg text-[13px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
        isDestructive(a)
          ? 'text-red-600 hover:bg-red-50'
          : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
      } ${a.className || ''}`}
    >
      {a.label}
    </button>
  );

  return (
    <div className={`relative inline-block ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        title={title}
        data-testid={dataTestId}
        className={`${buttonClassName || 'btn-outline !px-3 !py-1 !text-xs'} ${open ? '!bg-gray-100' : ''}`}
      >
        {icon}
        {label}
        <svg
          className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-1.5 w-44 bg-white border border-gray-100 rounded-xl shadow-lg ring-1 ring-black/5 z-10 overflow-hidden"
        >
          <div className="p-1.5">{primary.map(renderAction)}</div>
          {destructive.length > 0 && (
            <div className="p-1.5 border-t border-gray-100">{destructive.map(renderAction)}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default ActionDropdown;
